import { useEffect, useState } from "react";
import Sidebar from "../components/Sidebar";
import { CLIENT_CODES } from "../services/financeCore/clientTaxProfileService";
import { WHT_CONTROL_STATUSES, listWhtControlRows, updateWhtControlRecord } from "../services/financeCore/whtControlService";
import { getGstControlSummary, updateGstControlPeriod, addGstAdjustment } from "../services/financeCore/gstControlService";

const CLIENT_LIST = Object.values(CLIENT_CODES);
const WHT_STATUS_LIST = Object.values(WHT_CONTROL_STATUSES);
// Shared with AccountsReceivable.js so a client picked on one Management
// Finance page carries over to the others.
const LAST_CLIENT_KEY = "financeSelectedClient";
const money = (value) => `PKR ${Math.round(Number(value || 0)).toLocaleString("en-PK")}`;

function WhtControlTab() {
  const [clientCode, setClientCode] = useState(() => {
    const saved = localStorage.getItem(LAST_CLIENT_KEY);
    return CLIENT_LIST.includes(saved) ? saved : CLIENT_LIST[0];
  });
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");

  const load = async (code) => {
    setLoading(true);
    try {
      setRows(await listWhtControlRows(code));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(clientCode); }, [clientCode]);

  async function save(row, updates) {
    setMessage("");
    try {
      await updateWhtControlRecord(row.id, updates);
      setMessage(`${row.invoiceNumber} updated.`);
      await load(clientCode);
    } catch (error) {
      setMessage(error.message || "Could not update this record.");
    }
  }

  return (
    <section className="panel">
      <div className="panel-header">
        <h2 className="panel-title">WHT Control</h2>
        <div className="field" style={{ minWidth: 180 }}>
          <label>Client</label>
          <select className="input" value={clientCode} onChange={(e) => { setClientCode(e.target.value); localStorage.setItem(LAST_CLIENT_KEY, e.target.value); }}>
            {CLIENT_LIST.map((code) => <option key={code} value={code}>{code}</option>)}
          </select>
        </div>
      </div>
      {message && <p className="page-copy" role="status">{message}</p>}
      {loading ? <div className="panel-body page-copy">Loading…</div> : (
        <div className="table-wrap">
          <table className="data-table">
            <thead>
              <tr>
                <th>Invoice</th><th>Invoice Date</th><th>WHT Model</th><th>Expected WHT</th><th>Actual Deducted</th>
                <th>Certificate #</th><th>Certificate Date</th><th>Tax Year</th><th>Classification</th>
                <th>Amount Claimed</th><th>Remaining</th><th>Status</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => (
                <tr key={row.id}>
                  <td>{row.invoiceNumber}</td>
                  <td>{row.invoiceDate}</td>
                  <td>{row.whtModel}</td>
                  <td>{money(row.expectedWht)}</td>
                  <td>{money(row.actualWhtDeducted)}</td>
                  <td><input className="input" defaultValue={row.certificateNumber || ""} onBlur={(e) => save(row, { certificateNumber: e.target.value })} /></td>
                  <td><input className="input" type="date" defaultValue={row.certificateDate || ""} onBlur={(e) => save(row, { certificateDate: e.target.value })} /></td>
                  <td><input className="input" style={{ width: 90 }} defaultValue={row.taxYear || ""} onBlur={(e) => save(row, { taxYear: e.target.value })} /></td>
                  <td>
                    <select className="input" defaultValue={row.classification || "ADJUSTABLE"} onChange={(e) => save(row, { classification: e.target.value })}>
                      <option value="ADJUSTABLE">Adjustable</option>
                      <option value="FINAL">Final</option>
                    </select>
                  </td>
                  <td><input className="input" type="number" defaultValue={row.amountClaimed || 0} onBlur={(e) => save(row, { amountClaimed: e.target.value })} /></td>
                  <td>{money(row.amountRemaining)}</td>
                  <td>
                    <select className="input" defaultValue={row.status || "EXPECTED"} onChange={(e) => save(row, { status: e.target.value })}>
                      {WHT_STATUS_LIST.map((status) => <option key={status} value={status}>{status}</option>)}
                    </select>
                  </td>
                </tr>
              ))}
              {!rows.length && <tr><td colSpan={12}>No approved invoices for {clientCode} yet.</td></tr>}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

function GstControlTab() {
  const [monthKey, setMonthKey] = useState(new Date().toISOString().slice(0, 7));
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [adjustment, setAdjustment] = useState({ type: "CREDIT", amount: "", reason: "" });

  const load = async (month) => {
    setLoading(true);
    try {
      setSummary(await getGstControlSummary(month));
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(monthKey); }, [monthKey]);

  async function saveField(field, value) {
    setMessage("");
    try {
      await updateGstControlPeriod(monthKey, { [field]: field === "inputGstCredit" || field === "gstDeposited" ? Number(value) : value });
      await load(monthKey);
    } catch (error) {
      setMessage(error.message || "Could not update GST control.");
    }
  }

  async function submitAdjustment(event) {
    event.preventDefault();
    setMessage("");
    try {
      await addGstAdjustment(monthKey, adjustment);
      setAdjustment({ type: "CREDIT", amount: "", reason: "" });
      setMessage("Adjustment recorded.");
      await load(monthKey);
    } catch (error) {
      setMessage(error.message || "Could not record the adjustment.");
    }
  }

  return (
    <section className="panel">
      <div className="panel-header">
        <h2 className="panel-title">GST Control</h2>
        <div className="field" style={{ minWidth: 140 }}>
          <label>Month</label>
          <input className="input" type="month" value={monthKey} onChange={(e) => setMonthKey(e.target.value)} />
        </div>
      </div>
      {message && <p className="page-copy" role="status">{message}</p>}
      {loading || !summary ? <div className="panel-body page-copy">Loading…</div> : (
        <div className="panel-body">
          <table className="data-table">
            <tbody>
              <tr><td>Output GST charged (from approved invoices)</td><td>{money(summary.outputGst)}</td></tr>
              <tr><td>GST withheld by clients (recognized via receipts)</td><td>{money(summary.recognizedGstWithheld)}</td></tr>
              <tr><td>Input GST credit</td><td><input className="input" type="number" defaultValue={summary.inputGstCredit} onBlur={(e) => saveField("inputGstCredit", e.target.value)} /></td></tr>
              <tr><td>GST deposited</td><td><input className="input" type="number" defaultValue={summary.gstDeposited} onBlur={(e) => saveField("gstDeposited", e.target.value)} /></td></tr>
              <tr><td>Filing reference</td><td><input className="input" defaultValue={summary.filingReference} onBlur={(e) => saveField("filingReference", e.target.value)} /></td></tr>
              <tr><td>Payment date</td><td><input className="input" type="date" defaultValue={summary.paymentDate || ""} onBlur={(e) => saveField("paymentDate", e.target.value)} /></td></tr>
              <tr><td>Status</td><td>
                <select className="input" defaultValue={summary.status} onChange={(e) => saveField("status", e.target.value)}>
                  <option value="OPEN">Open</option><option value="FILED">Filed</option><option value="PAID">Paid</option>
                </select>
              </td></tr>
              <tr><td><strong>GST Balance (payable if positive)</strong></td><td><strong>{money(summary.balance)}</strong></td></tr>
            </tbody>
          </table>

          <h3 style={{ marginTop: 20 }}>Adjustments</h3>
          <table className="data-table">
            <thead><tr><th>Type</th><th>Amount</th><th>Reason</th><th>Entered By</th></tr></thead>
            <tbody>
              {(summary.adjustments || []).map((adj, index) => (
                <tr key={index}><td>{adj.type}</td><td>{money(adj.amount)}</td><td>{adj.reason}</td><td>{adj.enteredByEmail}</td></tr>
              ))}
              {!summary.adjustments?.length && <tr><td colSpan={4}>No adjustments recorded.</td></tr>}
            </tbody>
          </table>
          <form className="form-grid" style={{ marginTop: 12 }} onSubmit={submitAdjustment}>
            <div className="field"><label>Type</label>
              <select className="input" value={adjustment.type} onChange={(e) => setAdjustment({ ...adjustment, type: e.target.value })}>
                <option value="CREDIT">Credit</option><option value="DEBIT">Debit</option>
              </select>
            </div>
            <div className="field"><label>Amount</label>
              <input className="input" type="number" value={adjustment.amount} onChange={(e) => setAdjustment({ ...adjustment, amount: e.target.value })} />
            </div>
            <div className="field"><label>Reason</label>
              <input className="input" value={adjustment.reason} onChange={(e) => setAdjustment({ ...adjustment, reason: e.target.value })} />
            </div>
            <div className="field" style={{ alignSelf: "end" }}>
              <button className="btn btn-primary" type="submit">Add Adjustment</button>
            </div>
          </form>
        </div>
      )}
    </section>
  );
}

export default function WhtGstControl() {
  const [tab, setTab] = useState("WHT");
  return (
    <div className="app-shell">
      <Sidebar />
      <main className="main-content">
        <header className="page-header">
          <div>
            <div className="eyebrow">Management Finance</div>
            <h1 className="page-title">WHT &amp; GST Control</h1>
            <p className="page-copy">
              WHT and GST withheld are never treated as an expense or reduced revenue — they're tracked here as
              separate control balances against what receipts have actually recognized.
            </p>
          </div>
          <div className="button-row" style={{ margin: 0, borderTop: 0, padding: 0 }}>
            <button className={`btn ${tab === "WHT" ? "btn-primary" : "btn-muted"}`} type="button" onClick={() => setTab("WHT")}>WHT Control</button>
            <button className={`btn ${tab === "GST" ? "btn-primary" : "btn-muted"}`} type="button" onClick={() => setTab("GST")}>GST Control</button>
          </div>
        </header>
        {tab === "WHT" ? <WhtControlTab /> : <GstControlTab />}
      </main>
    </div>
  );
}
